import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";

import {
  RESOLVE_LOCAL_AUTH_STATE,
  RESOLVE_LOCAL_USER_STATE,
} from "../auth/AuthActions";
import Config from "../../config/Config.js";

const inputStyle = {
  backgroundColor: "#000000e6",
  color: "#59c6c1",
  borderColor: "#334155",
  padding: "8px 10px",
  fontSize: 14,
  width: "100%",
  borderRadius: 8,
  border: "1px solid #334155",
};

const labelStyle = { fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 3, display: "block" };

export default function ProfileChange() {
  const auth = useMemo(() => RESOLVE_LOCAL_AUTH_STATE(), []);
  const user = useMemo(() => RESOLVE_LOCAL_USER_STATE(), []);

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [bio, setBio] = useState("");
  const [website, setWebsite] = useState("");
  const [pictureLink, setPictureLink] = useState("");

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const initialRef = useRef({});

  const isValidUrl = (val) => {
    if (!val) return true;
    try {
      const normalized = val.match(/^https?:\/\//i) ? val : `https://${val}`;
      new URL(normalized);
      return true;
    } catch {
      return false;
    }
  };

  const normUrl = (val) => {
    if (!val) return "";
    return val.match(/^https?:\/\//i) ? val : `https://${val}`;
  };

  const isValidEmail = (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  // --- fetch profile ---
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${Config().extendedUserUrl}${user.pk}`);
        if (!active) return;
        const d = res.data;
        setBio(d.bio || "");
        setWebsite(d.website || "");
        setPictureLink(d.picture_link || "");
        initialRef.current = {
          bio: d.bio || "",
          website: d.website || "",
          picture_link: d.picture_link || "",
          first_name: user?.first_name || "",
          last_name: user?.last_name || "",
          email: user?.email || "",
        };
      } catch (err) {
        console.error("Profile Fetch Error:", err);
        toast.error("Couldn't load profile.");
      } finally {
        if (active) setFetching(false);
      }
    })();
    return () => { active = false; };
  }, [user.pk, user?.first_name, user?.last_name, user?.email]);

  // --- save (PATCH only changed fields) ---
  const save = useCallback(async () => {
    if (!isValidUrl(website) || !isValidUrl(pictureLink)) {
      toast.error("Please enter valid URLs.");
      return;
    }
    if (email && !isValidEmail(email)) {
      toast.error("Please enter a valid email.");
      return;
    }

    const init = initialRef.current;
    const headers = { Authorization: `Token ${auth.token}` };

    const profileDiff = {};
    const curBio = bio.trim();
    const curWebsite = normUrl(website.trim());
    const curPicture = normUrl(pictureLink.trim());
    if (curBio !== (init.bio || "")) profileDiff.bio = curBio;
    if (curWebsite !== (init.website || "")) profileDiff.website = curWebsite;
    if (curPicture !== (init.picture_link || "")) profileDiff.picture_link = curPicture;

    const personalDiff = {};
    if (firstName.trim() !== (init.first_name || "")) personalDiff.first_name = firstName.trim();
    if (lastName.trim() !== (init.last_name || "")) personalDiff.last_name = lastName.trim();
    if (email.trim() !== (init.email || "")) personalDiff.email = email.trim();

    const hasProfile = Object.keys(profileDiff).length > 0;
    const hasPersonal = Object.keys(personalDiff).length > 0;

    if (!hasProfile && !hasPersonal) {
      toast("No changes to save.");
      return;
    }

    try {
      setLoading(true);
      const requests = [];

      if (hasProfile) {
        requests.push(
          axios.patch(`${Config().extendedUserUrl}${user.pk}/`, profileDiff, { headers })
            .then((res) => {
              if (res?.data?.website) localStorage.setItem("USER_WEBSITE", res.data.website);
              if (res?.data?.picture_link) localStorage.setItem("USER_AVATAR", res.data.picture_link);
              Object.assign(initialRef.current, {
                bio: res.data.bio || "",
                website: res.data.website || "",
                picture_link: res.data.picture_link || "",
              });
            })
        );
      }

      if (hasPersonal) {
        requests.push(
          axios.patch(`${Config().userUrl}${user.pk}/`, personalDiff, { headers })
            .then((res) => {
              if (res.data.email) localStorage.setItem("USER_EMAIL", res.data.email);
              if (res.data.first_name) localStorage.setItem("USER_FIRST_NAME", res.data.first_name);
              if (res.data.last_name) localStorage.setItem("USER_LAST_NAME", res.data.last_name);
              Object.assign(initialRef.current, {
                first_name: res.data.first_name || "",
                last_name: res.data.last_name || "",
                email: res.data.email || "",
              });
            })
        );
      }

      await Promise.all(requests);
      toast.success("Saved");
    } catch (err) {
      console.error("Profile Save Error:", err);
      let msg = "Save failed.";
      if (err.response?.status === 400) {
        if (err.response?.data?.email) msg = "This email is already in use or invalid.";
        else msg = "Invalid data.";
      } else if (err.response?.status === 403) msg = "Permission denied.";
      else if (err.response?.status === 500) msg = "Server error.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [bio, website, pictureLink, firstName, lastName, email, user.pk, auth.token]);

  // Ctrl+S
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  if (fetching) {
    return <div style={{ padding: 32, color: "var(--muted)", fontSize: 13 }}>Loading…</div>;
  }

  const fieldRow = (label, value, onChange, type = "text", placeholder = "", extra) => (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={loading}
        style={inputStyle}
      />
      {extra}
    </div>
  );

  return (
    <div style={{ maxWidth: 520, margin: "32px auto", padding: "0 24px" }}>
      {/* Handle (read-only) */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Handle</label>
        <div style={{
          ...inputStyle,
          backgroundColor: "#0f172a",
          color: "#c9d1d9",
          cursor: "not-allowed",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span style={{ color: "#64748b" }}>@</span>
          <span>{user?.username || "—"}</span>
        </div>
      </div>

      {fieldRow("Email", email, setEmail, "email", "you@example.com",
        email && !isValidEmail(email) && (
          <small style={{ color: "var(--error)", fontSize: 11, marginTop: 2, display: "block" }}>Invalid email</small>
        )
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>First Name</label>
          <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
            placeholder="First" disabled={loading} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Last Name</label>
          <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
            placeholder="Last" disabled={loading} style={inputStyle} />
        </div>
      </div>

      {fieldRow("Bio", bio, setBio, "text", "A sentence or two about you")}

      {fieldRow("Website", website, setWebsite, "url", "yourdomain.com",
        website && !isValidUrl(website) && (
          <small style={{ color: "var(--error)", fontSize: 11, marginTop: 2, display: "block" }}>Invalid URL</small>
        )
      )}

      {/* Picture URL with preview */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Picture URL</label>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{
            width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
            border: "1px solid var(--border)", overflow: "hidden",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "var(--bg-neutral)",
          }}>
            {pictureLink && isValidUrl(pictureLink) ? (
              <img
                src={normUrl(pictureLink)}
                alt="Avatar"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <span style={{ fontSize: 9, color: "var(--muted)" }}>?</span>
            )}
          </div>
          <input
            type="url"
            value={pictureLink}
            onChange={(e) => setPictureLink(e.target.value)}
            placeholder="Link to a square image"
            disabled={loading}
            style={{ ...inputStyle, flex: 1 }}
          />
        </div>
        {pictureLink && !isValidUrl(pictureLink) && (
          <small style={{ color: "var(--error)", fontSize: 11, marginTop: 2, display: "block" }}>Invalid URL</small>
        )}
      </div>

      {/* Save */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
        <button
          type="button"
          onClick={save}
          disabled={loading}
          style={{
            background: "#59c6c1", border: "none", borderRadius: 6,
            color: "#0f172a", fontWeight: 600, fontSize: 13,
            padding: "8px 28px", cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
